import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import { DISCONNECT_PATH } from "./consts";
import { useAddRecentSandbox, useSandboxes } from "./hooks";
import { MainPanel } from "./MainPanel";
import { RemocalPill } from "./RemocalPill";
import { SandboxPicker } from "./SandboxPicker";
import * as styles from "./styles_vanilla.css";
import { buildSharableSandboxLink, getRoutingKeyFromCookie } from "./utils";

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const getFocusableElements = (container: HTMLElement): HTMLElement[] =>
  Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => el.offsetParent !== null,
  );

// window.location and its methods are [LegacyUnforgeable] per the WHATWG spec, so
// they can't be spied on in a real browser (e.g. Storybook). Inject navigation as
// a prop so stories/tests can capture it instead of hitting a dead link. External
// destinations (e.g. the linked PR on GitHub) open in a new tab; internal paths
// navigate in place.
const defaultNavigate = (url: string, options?: { external?: boolean }) => {
  if (options?.external) {
    window.open(url, "_blank", "noopener,noreferrer");
  } else {
    window.location.assign(url);
  }
};

export type RemocalWidgetProps = {
  onNavigate?: (url: string, options?: { external?: boolean }) => void;
};

export const RemocalWidget: React.FC<RemocalWidgetProps> = ({
  onNavigate = defaultNavigate,
}) => {
  const currentRoutingKey = getRoutingKeyFromCookie();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<"main" | "picker">("main");
  const [viewportHeight, setViewportHeight] = useState<number | undefined>(undefined);
  const { sandboxes, sandboxesLoading } = useSandboxes();

  const isBaseline = !currentRoutingKey;
  const currentSandbox = sandboxes?.find((sb) => sb.routingKey === currentRoutingKey);
  const isCurrentSandboxReady = currentSandbox?.ready ?? true;
  const currentSandboxNameDisplay = currentSandbox?.name || "Baseline";
  const initialSandboxesLoading = sandboxesLoading && sandboxes === undefined;

  const { addRecentSandbox } = useAddRecentSandbox();

  const handleSwitch = (routingKey: string) => {
    addRecentSandbox(routingKey);
    onNavigate(buildSharableSandboxLink(routingKey));
  };

  const panelRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const mainPaneRef = useRef<HTMLDivElement>(null);
  const pickerPaneRef = useRef<HTMLDivElement>(null);
  const pillRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLButtonElement | null>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  // Restore focus to the trigger pill once the panel closes.
  useEffect(() => {
    if (!open) return;
    return () => {
      previousFocusRef.current?.focus();
    };
  }, [open]);

  // Reset to the main view whenever the panel closes.
  useEffect(() => {
    if (!open) {
      setView("main");
      returnFocusRef.current = null;
    }
  }, [open]);

  // Remove the hidden pane from the tab order and accessibility tree so only the
  // visible view is reachable while the track is slid to the other pane.
  useEffect(() => {
    if (!open) return;
    if (mainPaneRef.current) mainPaneRef.current.inert = view !== "main";
    if (pickerPaneRef.current) pickerPaneRef.current.inert = view !== "picker";
  }, [open, view]);

  // Animate the viewport height to the active pane so the slide doesn't leave
  // empty space or clip content when the two views differ in height. A concrete
  // pixel height is required because CSS can't transition to/from `height: auto`;
  // the ResizeObserver keeps it correct as content (e.g. the sandbox list) loads.
  useLayoutEffect(() => {
    if (!open) return;
    const pane = view === "main" ? mainPaneRef.current : pickerPaneRef.current;
    if (!pane) return;
    const measure = () => setViewportHeight(pane.offsetHeight);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(pane);
    return () => observer.disconnect();
  }, [open, view, sandboxes, sandboxesLoading]);

  // Move focus into the active pane on open and on every view change. The inert
  // effect above runs first (effects fire in declaration order), so the target
  // pane is already interactive by the time we focus it.
  useEffect(() => {
    if (!open) return;
    // `preventScroll` is essential: the inactive pane sits off-screen inside the
    // clipped viewport, and a plain focus() would scroll it into view instantly,
    // jumping past the slide animation. We let the transform own positioning.
    if (view === "picker") {
      const picker = pickerPaneRef.current;
      if (!picker) return;
      const input = picker.querySelector<HTMLElement>("input");
      (input ?? getFocusableElements(picker)[0] ?? picker).focus({ preventScroll: true });
      return;
    }
    const main = mainPaneRef.current;
    if (!main) return;
    const returnTarget = returnFocusRef.current;
    if (returnTarget && main.contains(returnTarget)) {
      returnTarget.focus({ preventScroll: true });
      returnFocusRef.current = null;
    } else {
      main.focus({ preventScroll: true });
    }
  }, [open, view]);

  // Close when the user presses outside the panel (e.g. back onto the page).
  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    if (!panel) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node) || panel.contains(target)) return;
      setOpen(false);
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [open]);

  // Trap Tab/Shift+Tab within the panel and handle Escape: from the picker
  // it returns to the main view, from the main view it closes the panel.
  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    if (!panel) return;

    const getPanelFocusables = (): HTMLElement[] =>
      getFocusableElements(panel).filter((el) => el.closest("[inert]") === null);

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        const target = event.target as Node | null;
        if (!target || !panel.contains(target)) return;
        event.preventDefault();
        if (view === "picker") setView("main");
        else setOpen(false);
        return;
      }

      if (event.key !== "Tab") return;

      const items = getPanelFocusables();
      if (items.length === 0) {
        event.preventDefault();
        return;
      }

      const active = document.activeElement;
      const activeIndex = active instanceof HTMLElement ? items.indexOf(active) : -1;

      // Always preventDefault and move focus ourselves so DOM order cannot
      // carry Tab outside the panel (e.g. after the last pane control).
      event.preventDefault();

      if (event.shiftKey) {
        const nextIndex = activeIndex <= 0 ? items.length - 1 : activeIndex - 1;
        items[nextIndex]!.focus({ preventScroll: true });
      } else {
        const nextIndex =
          activeIndex === -1 || activeIndex >= items.length - 1 ? 0 : activeIndex + 1;
        items[nextIndex]!.focus({ preventScroll: true });
      }
    };

    // If focus somehow leaves (e.g. browser chrome or a portaled node), pull it
    // back so the panel stays the only interactive region while open.
    const handleFocusIn = (event: FocusEvent) => {
      const target = event.target;
      if (!(target instanceof Node) || panel.contains(target)) return;
      const items = getPanelFocusables();
      (items[0] ?? panel).focus({ preventScroll: true });
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("focusin", handleFocusIn);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("focusin", handleFocusIn);
    };
  }, [open, view]);

  return (
    <div className={styles.containerStyle}>
      {open ? (
        <div
          ref={panelRef}
          className={styles.panelStyle}
          role="dialog"
          aria-label="Remocal"
          aria-modal="false"
          tabIndex={-1}
        >
          <div
            className={styles.viewportStyle}
            style={assignInlineVars({
              [styles.viewportHeightVar]: viewportHeight ? `${viewportHeight}px` : "auto",
            })}
          >
            <div
              ref={mainPaneRef}
              className={styles.mainPaneVariants[view === "main" ? "active" : "inactive"]}
              data-testid="remocal-main-pane"
              tabIndex={-1}
            >
              <MainPanel
                active={view === "main"}
                sandboxes={sandboxes}
                sandboxesLoading={sandboxesLoading}
                currentRoutingKey={currentRoutingKey}
                onClose={() => setOpen(false)}
                onOpenPicker={(returnFocus) => {
                  returnFocusRef.current = returnFocus;
                  setView("picker");
                }}
                onSwitch={handleSwitch}
                onDisconnect={() => onNavigate(DISCONNECT_PATH)}
                closeButtonRef={closeButtonRef}
              />
            </div>
            <div
              ref={pickerPaneRef}
              className={styles.pickerPaneVariants[view === "picker" ? "active" : "inactive"]}
              data-testid="remocal-picker-pane"
              tabIndex={-1}
            >
              <SandboxPicker
                active={view === "picker"}
                onBack={() => setView("main")}
                onClose={() => setOpen(false)}
                onSwitch={handleSwitch}
                sandboxes={sandboxes}
                sandboxesLoading={sandboxesLoading}
                currentRoutingKey={currentRoutingKey}
                closeButtonRef={closeButtonRef}
              />
            </div>
          </div>
        </div>
      ) : (
        <RemocalPill
          ref={pillRef}
          isLoading={initialSandboxesLoading}
          isCurrentSandboxReady={isCurrentSandboxReady}
          isBaseline={isBaseline}
          label={currentSandboxNameDisplay}
          onOpen={() => {
            previousFocusRef.current = pillRef.current;
            setOpen(true);
          }}
        />
      )}
    </div>
  );
};
